import { ForwardedRef, forwardRef, useImperativeHandle, useState } from 'react';
import {
  OptionEntity,
  useCreateOptionMutation,
  useUpdateOptionMutation,
  useDeleteOptionMutation,
} from 'src/store/optionApi';
import { setOption as setOptionAction } from 'src/store/optionSlice';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import ProfileFieldSelector from 'src/component/profileFiledSelector';
import { Button } from 'src/ui/Button';
import { Input } from 'src/ui/Input';
import { Icon } from 'src/ui/Icon';
import { HelpTip } from 'src/ui/Tooltip';
import { useToast } from 'src/ui/Toast';

export interface EditorProps {
  refetchAll: () => void;
}

export interface EditorRef {
  onCreate: () => void;
  onSelect: (dbOption: OptionEntity) => void;
}

const blankOption = (): OptionEntity => ({
  id: 0,
  field: '',
  labels: [],
});

/**
 * The languages a label carries. These keys are what the API reads —
 * OnboardingService picks `labels[lang]` and falls back to `en` then `cn`, so
 * a label left blank here silently serves the other language's text.
 */
const LANGUAGES = [
  { key: 'cn', label: '中文', placeholder: '选项文字，例如：是' },
  { key: 'en', label: 'EN', placeholder: 'Label text, e.g. Yes' },
];

const Editor = forwardRef(
  ({ refetchAll }: EditorProps, ref: ForwardedRef<EditorRef>) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const [option, setOption] = useState<OptionEntity>(blankOption());
    const [errors, setErrors] = useState<Record<string, string>>({});

    const [createOption, { isLoading: isCreating }] = useCreateOptionMutation();
    const [updateOption, { isLoading: isUpdating }] = useUpdateOptionMutation();
    const [deleteOption, { isLoading: isDeleting }] = useDeleteOptionMutation();

    useImperativeHandle(ref, () => ({
      onCreate: () => {
        setOption(blankOption());
      },
      onSelect: (dbOption: OptionEntity) => {
        setOption(_.cloneDeep(dbOption));
      },
    }));

    const onChange = (key: string, value: string | number | boolean) => {
      const newOption: any = { ...option };
      const keys = key.split('.');
      if (keys.length === 1) {
        newOption[key] = value;
      } else {
        newOption[keys[0]][keys[1]] = value;
      }
      setOption(newOption);
    };

    const onChangeLabelText = (idx: number, lang: string, value: string) => {
      const newOption: OptionEntity = { ...option };
      if (!newOption.labels) {
        newOption.labels = [];
      }
      if (!newOption.labels[idx]) {
        newOption.labels[idx] = [idx + 1, {}];
      }
      const [key, texts] = newOption.labels[idx];
      // Replace the label object rather than mutating it, so the other
      // language's text is carried over explicitly instead of by reference.
      newOption.labels[idx] = [key, { ...texts, [lang]: value }];
      setOption(newOption);
    };

    const onChangeLabelValue = (idx: number, value: string) => {
      const newOption: OptionEntity = { ...option };
      if (!newOption.labels) {
        newOption.labels = [];
      }
      if (!newOption.labels[idx]) {
        newOption.labels[idx] = [idx + 1, {}];
      }
      newOption.labels[idx][0] = value === '-' ? -1 : Number(value);
      setOption(newOption);
    };

    const onDeleteLabel = (idx: number) => {
      const newOption: OptionEntity = { ...option };
      if (!newOption.labels) {
        newOption.labels = [];
      }
      newOption.labels.splice(idx, 1);
      setOption(newOption);
      refetchAll();
    };

    const onSave = async () => {
      if (!option) return;
      const newErrors: Record<string, string> = {};
      if (!option.field) {
        newErrors.field = 'Field is required';
      }
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }
      setErrors({});
      try {
        let newOption;
        if (option.id) {
          newOption = await updateOption(option).unwrap();
        } else {
          newOption = await createOption(option).unwrap();
        }
        setOption(_.cloneDeep(newOption));
        dispatch(setOptionAction(newOption));
        toast({
          title: 'Option saved',
          type: 'success',
          duration: 5000,
        });
      } catch (e: any) {
        console.log(e);
        toast({
          title: 'Failed to save option',
          type: 'error',
          duration: 5000,
          message: e.data?.message || e.message || e.toString(),
        });
      }
    };

    const onDelete = async () => {
      await deleteOption(option.id).unwrap();
      toast({
        title: 'Option deleted',
        type: 'success',
        duration: 5000,
      });
      refetchAll();
    };

    const renderLabel = (
      [key, value]: [number, Record<string, string>],
      idx: number
    ) => {
      return (
        <div
          key={idx}
          className="col gap-2"
          style={{
            padding: '12px 0',
            borderBottom: '1px solid var(--border)',
          }}
        >
          {/* One row per language. Both are stored on the same label; the app
              falls back to Chinese when a language is blank, so an empty
              English box is what shipped Chinese text to English users. */}
          {LANGUAGES.map(({ key: lang, label, placeholder }) => (
            <div
              key={lang}
              className="row gap-2"
              style={{ alignItems: 'center' }}
            >
              <span className="eyebrow" style={{ width: 48, flexShrink: 0 }}>
                {label}
              </span>
              <Input
                size="sm"
                value={value[lang] || ''}
                onChange={(e) => onChangeLabelText(idx, lang, e.target.value)}
                placeholder={placeholder}
                style={{ flex: 1 }}
              />
            </div>
          ))}
          {/* Value + delete row */}
          <div className="row gap-2" style={{ alignItems: 'center' }}>
            <span
              className="row gap-1 eyebrow"
              style={{ width: 48, flexShrink: 0, alignItems: 'center' }}
            >
              Value
              <HelpTip label="Starting from 1. Set 0 for other" />
            </span>
            <Input
              size="sm"
              type="number"
              min="-1"
              value={key}
              onChange={(e) => onChangeLabelValue(idx, e.target.value)}
              style={{ flex: 1 }}
            />
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              title="Delete row"
              onClick={() => onDeleteLabel(idx)}
            >
              <Icon name="trash" size={14} />
            </Button>
          </div>
        </div>
      );
    };

    return (
      <div className="col" style={{ overflow: 'hidden' }}>
        {/* Editor header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--border)',
            flexShrink: 0,
          }}
        >
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <div>
              <h2 className="section-title">
                {option.id ? option.field || 'Edit' : 'New option set'}
              </h2>
              {option.id > 0 && (
                <div
                  className="text-muted"
                  style={{ fontSize: 'var(--fs-sm)', marginTop: 2 }}
                >
                  {option.labels.length} values
                </div>
              )}
            </div>
            {/* Profile field selector */}
            <div style={{ width: 240 }}>
              <label
                className="field-label row gap-1"
                style={{ marginBottom: 4 }}
              >
                Profile field{' '}
                <HelpTip label="Which profile attribute these options populate" />
              </label>
              <ProfileFieldSelector
                value={option.field}
                onChange={(value) => onChange('field', value)}
                supportRange
              />
              {errors.field && (
                <div
                  style={{
                    color: 'var(--red-text)',
                    fontSize: 'var(--fs-xs)',
                    marginTop: 4,
                  }}
                >
                  {errors.field}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Labels area */}
        <div className="grow scroll-y" style={{ padding: 20 }}>
          <div style={{ maxWidth: 720 }}>
            <div className="row gap-1" style={{ marginBottom: 10 }}>
              <span className="eyebrow">Label key &amp; values</span>
              <HelpTip label="Key is the stored value; CN is shown to users by locale." />
            </div>

            <div className="col gap-0">{option.labels?.map(renderLabel)}</div>

            <Button
              variant="ghost"
              size="sm"
              leftIcon="plus"
              style={{ alignSelf: 'flex-start', marginTop: 12 }}
              onClick={() =>
                onChangeLabelValue(
                  option.labels.length,
                  String(option.labels.length + 1)
                )
              }
            >
              Add value
            </Button>
          </div>
        </div>

        {/* Footer actions */}
        <div
          style={{
            padding: '14px 20px',
            borderTop: '1px solid var(--border)',
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {option.id > 0 ? (
            <Button
              variant="danger-ghost"
              leftIcon="trash"
              loading={isDeleting}
              onClick={() => {
                if (
                  window.confirm(
                    'Are you sure you want to delete this option? This action is irreversible.'
                  )
                ) {
                  onDelete();
                }
              }}
            >
              Delete option set
            </Button>
          ) : (
            <span />
          )}
          <div className="row gap-2">
            <Button variant="ghost" onClick={() => setOption(blankOption())}>
              Discard
            </Button>
            <Button
              variant="primary"
              leftIcon="save"
              loading={isCreating || isUpdating}
              onClick={onSave}
            >
              Save changes
            </Button>
          </div>
        </div>
      </div>
    );
  }
);

export default Editor;
